import Navbar from "./components/Navbar";
import FooterSection from "./components/FooterSection";
import ServiceVisual from "./components/ServiceVisual";

export default function NewFourGeoPage() {
  return (
    <div className="min-h-screen bg-[#fff8ef] text-[#171717] overflow-hidden">
      <div
        className="fixed inset-0 pointer-events-none opacity-70"
        style={{
          background:
            "radial-gradient(circle at 12% 18%, rgba(255,126,78,0.26), transparent 18%), radial-gradient(circle at 82% 14%, rgba(89,170,255,0.24), transparent 20%), radial-gradient(circle at 74% 68%, rgba(255,205,82,0.22), transparent 18%), radial-gradient(circle at 18% 74%, rgba(102,238,201,0.18), transparent 16%)",
        }}
      />
      <Navbar />
      <main className="relative z-10 pt-[110px] pb-20">
        <section className="pb-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/75 border border-[#171717]/10">
                <span className="w-2 h-2 rounded-full bg-[#59aaff]"></span>
                <span className="text-xs font-bold tracking-[0.18em] uppercase">Service • GEO</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-black tracking-[-0.06em] leading-[0.94] max-w-4xl mb-5">
                Make Flowvory visible where users <span className="text-[#59aaff]">ask AI</span>, compare options, and choose.
              </h1>
              <p className="text-[#4f4f4f] text-lg leading-relaxed max-w-2xl mb-8">
                GEO is not a keyword facelift. It is a visibility system for answer engines, entity-driven discovery, and recommendation layers that now shape commercial intent before the click.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => window.dispatchEvent(new Event("flowvory:open-strategy"))} className="px-7 py-4 rounded-full bg-[#171717] text-[#fff8ef] text-base font-bold shadow-[8px_8px_0_rgba(89,170,255,0.24)]">
                  Book a Call
                </button>
                <div className="px-6 py-4 rounded-[22px] bg-white/78 border border-[#171717]/10 text-sm font-semibold shadow-[8px_8px_0_rgba(255,126,78,0.14)]">
                  Built for operators who want visibility systems with real commercial depth behind them.
                </div>
              </div>
            </div>
            <div className="rounded-[36px] border border-[#171717]/10 bg-white/78 p-7 shadow-[0_30px_100px_rgba(23,23,23,0.08)]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-2">What changes when GEO becomes a system, not a page-level tactic</p>
                  <h2 className="text-2xl font-black tracking-[-0.05em]">From being indexable to being usable inside AI answers</h2>
                </div>
                <div className="w-12 h-12 rounded-[18px] bg-[#171717] text-[#fff8ef] flex items-center justify-center">
                  <i className="ri-bubble-chart-line text-xl"></i>
                </div>
              </div>
              <div className="space-y-5">
                {[
                  ["Traditional SEO only", "48", "Pages can rank, but AI still struggles to connect the brand to the right recommendation context"],
                  ["Entity + GEO structure", "81", "The business becomes easier to retrieve, interpret, and synthesize inside answer interfaces"],
                  ["Commercial reinforcement", "92", "Trust signals, comparisons, and supporting context make inclusion easier to justify"],
                ].map(([label, score, note], idx) => (
                  <div key={label} className={`${idx === 1 ? "rotate-[-1.4deg] bg-[#171717] text-white" : "bg-[#f8f8f5] text-[#171717]"} rounded-[24px] border border-[#171717]/10 p-5`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-[0.18em] font-bold">{label}</p>
                      <span className={`text-2xl font-black ${idx === 1 ? "text-[#59aaff]" : "text-[#171717]"}`}>{score}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-black/10 overflow-hidden mb-3">
                      <div className={`h-full rounded-full ${idx === 1 ? "bg-[#59aaff]" : idx === 2 ? "bg-[#66eec9]" : "bg-[#ffd14f]"}`} style={{ width: `${score}%` }}></div>
                    </div>
                    <p className={`text-sm leading-relaxed ${idx === 1 ? "text-white/75" : "text-[#4f4f4f]"}`}>{note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.08fr_0.92fr] gap-6 items-start">
            <div className="rounded-[32px] border border-[#171717]/10 bg-white/88 p-7 shadow-[0_18px_50px_rgba(23,23,23,0.05)]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">How Flowvory usually scopes a GEO engagement</p>
              <h2 className="text-3xl font-black tracking-[-0.05em] leading-tight mb-4">The work usually starts by making three things easier for AI systems to trust.</h2>
              <p className="text-[#4f4f4f] leading-relaxed mb-6 max-w-3xl">This is usually the difference between a brand that is technically indexable and a brand that repeatedly appears in AI-led discovery. Flowvory does not treat these as separate channel tasks. They are one shared visibility model.</p>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  ["Entity clarity", "If the brand is hard to describe cleanly, AI systems struggle to recommend it confidently."],
                  ["Answer-surface coverage", "Flowvory maps the questions, comparisons, and contexts where the brand should show up."],
                  ["Commercial proof", "The job is not visibility alone. The signals also need to support trust when AI compresses the market."],
                ].map(([title, text], idx) => (
                  <article key={title} className={`rounded-[28px] border border-[#171717]/10 p-5 ${idx === 1 ? "bg-[#dff3ff]" : idx === 2 ? "bg-[#fff0db]" : "bg-[#f8f8f5]"}`}>
                    <h3 className="text-xl font-black tracking-[-0.03em] leading-snug mb-3">{title}</h3>
                    <p className="text-[#4f4f4f] leading-relaxed text-sm">{text}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="rounded-[32px] border border-[#171717]/10 bg-[#171717] text-[#fff8ef] p-7 shadow-[0_18px_50px_rgba(23,23,23,0.10)]">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">Where weak GEO usually shows up first</p>
              <div className="space-y-4">
                {[
                  ["AI answers mention competitors first", "The retrieval layer has more confidence in their category fit and supporting evidence."],
                  ["Brand pages rank but do not get cited", "The pages are crawlable, but the entity and offer structure are still too vague for synthesis."],
                  ["Comparison intent goes elsewhere", "Commercial trust and differentiation are not explicit enough at the moment choices get compressed."],
                ].map(([title, text]) => (
                  <div key={title} className="rounded-[24px] border border-white/10 bg-white/6 p-4">
                    <h3 className="text-lg font-black tracking-[-0.03em] mb-2">{title}</h3>
                    <p className="text-sm leading-relaxed text-white/72">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1fr] gap-6 items-start">
            <ServiceVisual
              title="Where GEO usually breaks first"
              subtitle="These bars represent the three layers Flowvory usually checks first when a brand is technically present on the web but still absent in AI-led recommendation paths."
              bars={[
                { label: "Entity clarity", value: 84, color: "#59aaff" },
                { label: "Answer-surface fit", value: 69, color: "#ffd14f" },
                { label: "Commercial trust", value: 77, color: "#66eec9" },
              ]}
            />
            <div className="rounded-[30px] border border-[#171717]/10 bg-white/86 p-6 shadow-[0_18px_50px_rgba(23,23,23,0.05)] overflow-x-auto">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">The difference between being crawlable and being recommendable</p>
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="text-left border-b border-[#171717]/10">
                    <th className="pb-3 pr-4 font-bold">Layer</th>
                    <th className="pb-3 pr-4 font-bold">Weak setup</th>
                    <th className="pb-3 font-bold">Flowvory setup</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Entity signals", "Vague, fragmented", "Clear brand and category definition"],
                    ["Content support", "Isolated posts", "Reinforcing answer coverage"],
                    ["Commercial proof", "Buried or generic", "Visible, machine-friendly trust cues"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-[#171717]/8 align-top">
                      <td className="py-3 pr-4 font-semibold">{row[0]}</td>
                      <td className="py-3 pr-4 text-[#4f4f4f]">{row[1]}</td>
                      <td className="py-3 text-[#4f4f4f]">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-[#171717]/8">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.95fr_1.05fr] gap-6 items-start">
            <div className="rounded-[34px] bg-[#171717] text-[#fff8ef] p-8 lg:p-10 shadow-[0_18px_50px_rgba(23,23,23,0.12)]">
              <p className="text-[10px] uppercase tracking-[0.24em] text-white/45 font-bold mb-4">Why some brands get picked and others stay invisible</p>
              <blockquote className="text-2xl md:text-3xl font-black tracking-[-0.04em] leading-snug mb-6">
                “The brands that win in AI discovery are not the noisiest. They are the easiest to understand, verify, and recommend.”
              </blockquote>
              <button onClick={() => window.dispatchEvent(new Event("flowvory:open-strategy"))} className="px-6 py-3 rounded-full bg-[#fff8ef] text-[#171717] font-black text-sm shadow-[6px_6px_0_rgba(89,170,255,0.16)]">
                Book a Call
              </button>
            </div>
            <div className="rounded-[34px] border border-[#171717]/10 bg-white/84 p-7 shadow-[0_18px_50px_rgba(23,23,23,0.05)] overflow-x-auto">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#6b6b6b] font-bold mb-3">What changes between weak GEO and recommendation-grade GEO</p>
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left border-b border-[#171717]/10">
                    <th className="pb-3 pr-4 font-bold">Moment</th>
                    <th className="pb-3 pr-4 font-bold">Weak GEO</th>
                    <th className="pb-3 font-bold">Flowvory GEO</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Category understanding", "AI sees fragments of the offer", "AI can place the brand inside a clearer category frame"],
                    ["Comparison synthesis", "Competitors dominate shortlist logic", "Differentiators appear earlier in the comparison path"],
                    ["Trust compression", "Proof is generic or buried", "Trust signals support the recommendation moment directly"],
                    ["Commercial handoff", "Visibility and conversion feel disconnected", "Answer-surface visibility and sales context reinforce each other"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-[#171717]/8 align-top">
                      <td className="py-3 pr-4 font-semibold">{row[0]}</td>
                      <td className="py-3 pr-4 text-[#4f4f4f]">{row[1]}</td>
                      <td className="py-3 text-[#4f4f4f]">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="rounded-[34px] border border-[#ff7e4e]/25 bg-[linear-gradient(135deg,rgba(255,126,78,0.10),rgba(255,248,239,0.92))] p-7 lg:p-8 shadow-[0_18px_50px_rgba(23,23,23,0.05)]">
              <div className="grid lg:grid-cols-[1fr_0.9fr] gap-6 items-center">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#8a4b31] font-bold mb-3">A more practical way to start the conversation</p>
                  <h3 className="text-2xl md:text-3xl font-black tracking-[-0.04em] leading-snug mb-3">We keep the client roster intentionally small so the strategy work stays hands-on.</h3>
                  <p className="text-[#5c463a] leading-relaxed">If you want, we can treat this as a separate planning conversation rather than a standard funnel CTA. That is also why this note lives down here, not on the first screen.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="rounded-[22px] border border-[#171717]/10 bg-white/84 px-4 py-4 text-sm font-semibold">One lead client per quarter</div>
                  <div className="rounded-[22px] border border-[#171717]/10 bg-[#171717] text-[#fff8ef] px-4 py-4 text-sm font-semibold">Current availability: Q2</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
